"use client"

import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"

import {cn} from "@/utils/utils"
import { cva, type VariantProps } from "class-variance-authority"
import {Icons} from "@videohighlight/ui/icons"

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({className, ...props}, ref) => (
    <AccordionPrimitive.Item
        ref={ref}
        className={cn("border-b", className)}
        {...props}
    />
))
AccordionItem.displayName = "AccordionItem"

// Define variants for the chevron orientation
const chevronOrientationVariants = cva(
    "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200", {
        variants: {
            orientation: {
                vertical: "[&>svg]:rotate-270",
                horizontal: "[&>svg]:rotate-0",
            },
        },
        defaultVariants: {
            orientation: "vertical",
        },
    });

// Extend props to include orientation variants
interface AccordionTriggerProps extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>, VariantProps<typeof chevronOrientationVariants> {
    orientation?: 'horizontal' | 'vertical';
}

const AccordionTrigger = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    AccordionTriggerProps
>(({ className, children, orientation = 'vertical', ...props }, ref) => (
    <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
            ref={ref}
            className={cn(
                "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline",
                orientation === 'horizontal' ? "[&[data-state=open]>svg]:rotate-90" : "[&[data-state=open]>svg]:rotate-90",
                className
            )}
            {...props}
        >
            {children}
            <Icons.chevronRight className={chevronOrientationVariants({ orientation })}/>
        </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({className, children, ...props}, ref) => (
    <AccordionPrimitive.Content
        ref={ref}
        className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
        {...props}
    >
        <div className={cn("pb-4 pt-0", className)}>{children}</div>
    </AccordionPrimitive.Content>
))
AccordionContent.displayName = AccordionPrimitive.Content.displayName

export {Accordion, AccordionItem, AccordionTrigger, AccordionContent}
